// speakers — turning the transcriber's diarization into something a
// reader can follow (T-01, transcriber 0.18.0).
//
// The backend says "S1", "S2" and, sometimes, a gender guess and a name.
// The screen has to turn that into "דוברת ג׳" for a Hebrew reader and
// "Speaker C" for an English one, and it has to stop repeating itself
// on every line of a two-minute answer.
//
// ─── Three decisions worth stating ──────────────────────────────────
//
// 1. `speakersReconciled === null` suppresses labels entirely, and that
//    is the ticket's call, not an accident. Unreconciled numbering means
//    "S1" in chunk 3 may be a different person from "S1" in chunk 1 —
//    and a reader cannot tell a trustworthy label from an untrustworthy
//    one by looking at it. `false` is different: there the backend
//    positively says the numbering may restart, so the labels are shown
//    WITH that warning. Silence about the question is not the same as an
//    answer to it.
//
// 2. A null gender reads as masculine, because Hebrew and Russian force
//    a choice. That is a defensible default rather than a correct one —
//    see `speakerWordKey`.
//
// 3. A gap in the transcript (a chunk that failed) breaks a speaker run.
//    The label repeats after it. Whoever was talking before the gap may
//    not be who is talking after it, and even if they are, the reader
//    lost the thread.
//
// Pure module: no React, no DOM. The alphabets live in the dictionary
// rather than here, so adding a language is a translation change and
// not a code change — same reasoning as `relLocale` in F-04.

import type { SegmentSpeaker, TranslationJob, TranslationSpeaker } from '@nayto/types';

/** How the transcript should treat speakers for one job. */
export type SpeakerMode =
  /** No labels at all: an old job, or reconciliation never answered. */
  | 'off'
  /** Labels, and the numbering holds across the whole job. */
  | 'labelled'
  /** Labels, but the numbering may restart between chunks — say so. */
  | 'unreconciled';

export function speakerMode(job: Pick<TranslationJob, 'speakersReconciled'>): SpeakerMode {
  if (job.speakersReconciled === true) return 'labelled';
  if (job.speakersReconciled === false) return 'unreconciled';
  // null, undefined, or anything a future backend sends that is neither.
  return 'off';
}

/**
 * 'S1' → 0, 'S2' → 1, 'S12' → 11. Anything else → null.
 *
 * Deliberately strict: a label is a claim about who is speaking, and
 * inventing an ordinal from an id we do not recognise would make that
 * claim up.
 */
export function speakerOrdinal(id: string | null | undefined): number | null {
  if (typeof id !== 'string') return null;
  const m = /^S(\d+)$/.exec(id.trim());
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isInteger(n) && n >= 1 ? n - 1 : null;
}

/**
 * The letter for a speaker in the reader's alphabet.
 *
 * `spkLetters` is a comma-separated list in the dictionary — 'A,B,C…'
 * in English, 'א׳,ב׳,ג׳…' in Hebrew. Past the end of the list (a
 * 27-speaker recording) there is no letter to use, and the caller falls
 * back to the raw id rather than wrapping around to 'A' and claiming
 * two people are the same one.
 */
export function speakerLetter(ordinal: number, t: (key: string) => string): string | null {
  const letters = t('spkLetters').split(',');
  const letter = letters[ordinal]?.trim();
  return letter ? letter : null;
}

/** Which of the two gendered words to use. `null` and `'m'` both take
 * the masculine: Hebrew and Russian have no neutral form here, so an
 * absent guess has to land somewhere, and the masculine is the
 * conventional default in both. It is a default, not a truth — the
 * moment the backend supplies a name, the name wins and this stops
 * mattering. */
export function speakerWordKey(gender: 'm' | 'f' | null | undefined): string {
  return gender === 'f' ? 'spkFemale' : 'spkMale';
}

/**
 * The label for one segment's speaker, in the reader's language.
 *
 * A `name` from `job.speakers` always wins: "Nikol Pashinyan" tells the
 * reader more than "Speaker A" ever will, in any language.
 */
export function speakerLabel(
  speaker: SegmentSpeaker | null | undefined,
  roster: readonly TranslationSpeaker[] | null | undefined,
  t: (key: string) => string,
): string | null {
  if (!speaker || typeof speaker.id !== 'string' || speaker.id === '') return null;

  const entry = roster?.find((s) => s.id === speaker.id) ?? null;
  const name = entry?.name?.trim();
  if (name) return name;

  const ordinal = speakerOrdinal(speaker.id);
  const letter = ordinal === null ? null : speakerLetter(ordinal, t);
  // No letter we can use — show the raw id. It is not pretty, but it
  // distinguishes one speaker from another, which is the whole job.
  if (letter === null) return speaker.id;

  // The roster's gender is the job-wide answer and outranks the
  // segment's, which is per-chunk; fall back to the segment's when the
  // roster does not carry the speaker.
  const gender = entry ? entry.gender : speaker.gender;
  return `${t(speakerWordKey(gender))} ${letter}`;
}

/**
 * Which rows in a sequence start a new speaker run.
 *
 * Input is one entry per row: the speaker id, or `null` for a row that
 * has no speaker (a gap, or a segment from before 0.18.0). A null
 * always returns false — there is nothing to label — AND breaks the
 * run, so the next real row re-announces who is talking.
 *
 * Sequence-in, booleans-out, because the interesting bugs here are
 * about adjacency and adjacency is exactly what is hard to see in JSX.
 */
export function speakerRunStarts(ids: ReadonlyArray<string | null>): boolean[] {
  let previous: string | null = null;
  return ids.map((id) => {
    if (id === null) {
      previous = null;
      return false;
    }
    const isStart = id !== previous;
    previous = id;
    return isStart;
  });
}

/** The label a copied or printed transcript puts in front of a line.
 * Same rules as the screen — item 5 of the ticket is really "the two
 * must not drift", so they share this module rather than each
 * formatting their own. */
export function speakerPrefix(
  speaker: SegmentSpeaker | null | undefined,
  roster: readonly TranslationSpeaker[] | null | undefined,
  t: (key: string) => string,
): string {
  const label = speakerLabel(speaker, roster, t);
  return label ? `${label}: ` : '';
}
